/**
 * Toasts, the unread badge, and the inbox.
 *
 * One notification, three ways of showing it — a toast that interrupts, a
 * badge that counts, a row that lists — all rendered from the same shape so a
 * player never sees the same event described two different ways.
 */

import { seenPlan } from '@/net/badge';
import { el } from '@/ui/hud';
import { drawnLine, sentence, summaryText, suffixSep, amountText, scoresText } from '@/ui/notify-copy';
import { coin } from '@/ui/screens';
import type { AppNotification, NotificationKind } from '@/net/api';

/**
 * Colour is the verdict, and money is the only thing that is ever amber.
 *
 * WON mint, LOST rose, TAKEN in the match-level key (azure here — Blockari's
 * is indigo; the rule is "the game's match key", not a shared hue), EXPIRED
 * dim because nothing happened and nothing should shout about it. Amber is
 * reserved for $CHAIN so an amount is always legible as money.
 */
const TONE: Record<NotificationKind, string> = {
  challenge_won: 'won',
  challenge_lost: 'lost',
  challenge_taken: 'taken',
  challenge_expired: 'expired',
  challenge_voided: 'expired',
  // A chest is a reward, so it wears the money hue: amber is already the
  // colour of something being given to you.
  chest_earned: 'chest',
};

const EYEBROW: Record<NotificationKind, string> = {
  // WON / LOST as a PAIR. "You won" against a bare "Lost" is two different
  // voices for one axis, and the eyebrow is the axis.
  challenge_won: 'You won',
  challenge_lost: 'You lost',
  challenge_taken: 'Taken',
  challenge_expired: 'Expired',
  challenge_voided: 'Voided',
  chest_earned: 'You earned',
};

/** The short chip the inbox uses, where there is no room for a sentence. */
const CHIP: Record<NotificationKind, string> = {
  // No pronoun here — the chip is a label in a fixed 76px column, and "You
  // won" in a badge is a sentence pretending to be one. The TOAST's eyebrow
  // carries the pronoun; this does not.
  challenge_won: 'Won',
  challenge_lost: 'Lost',
  challenge_taken: 'Taken',
  challenge_expired: 'Expired',
  challenge_voided: 'Voided',
  chest_earned: 'Chest',
};

/** `just now`, `4 min ago`, `2 h ago`. */
function when(iso: string): string {
  const seconds = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)} h ago`;
  return `${Math.floor(seconds / 86_400)} d ago`;
}

/* ---------------------------------------------------------------- toasts -- */

/** A press that moved less than this is a tap; Blockari's `TAP_PX`. */
const TAP_PX = 6;
const TOAST_MS = 6000;
/** How long a new toast lets touches through to what is under it. */
const TOAST_ARMING_MS = 700;
const MAX_TOASTS = 3;

export interface ToastCallbacks {
  onOpen(challengeId: string): void;
  /** A chest notice opens the chest, not a challenge. */
  onOpenChest?(chestId: string): void;
  /** A toast is on screen: the money flight (a win's coin) leaves from its amount. */
  onShown?(n: AppNotification, toast: HTMLElement): void;
}

const ICON_X =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"></path></svg>';
const ICON_ARROW =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"></path></svg>';

/** The kit's small icon button: ✕ in a 40 px square (44 on a coarse pointer). */
function iconButton(label: string, onClick: () => void): HTMLButtonElement {
  const button = el('button', 'w21-icon-btn w21-sm') as HTMLButtonElement;
  button.type = 'button';
  button.setAttribute('aria-label', label);
  button.innerHTML = ICON_X;
  button.addEventListener('click', (event) => {
    event.stopPropagation();
    onClick();
  });
  return button;
}

/**
 * The ✕ on a toast: it dismisses and does nothing else.
 *
 * Its own handler, and nothing it touches reaches the toast: the click is stopped (and its default dropped), and so
 * are the pointer events the toast's swipe-to-dismiss listens to, so pressing the ✕ is never the start of a swipe or
 * a press on the toast's body. The whole column it sits in is the target (not a 40 px glyph in a corner), the button
 * itself is 44 px square on any pointer (see `.w21-nt-close` in parity.css), and it says what it does.
 */
function dismissControl(onDismiss: () => void): HTMLElement {
  const column = el('span', 'w21-nt-close');
  const button = el('button', 'w21-icon-btn w21-sm w21-nt-x') as HTMLButtonElement;
  button.type = 'button';
  button.setAttribute('aria-label', 'Dismiss notification');
  button.innerHTML = ICON_X;
  const stop = (event: Event): void => event.stopPropagation();
  for (const type of ['pointerdown', 'pointerup', 'pointercancel', 'pointermove'] as const) column.addEventListener(type, stop);
  column.addEventListener('click', (event) => {
    event.stopPropagation();
    event.preventDefault();
    onDismiss();
  });
  column.append(button);
  return column;
}

/** Kept for callers that still build the older close control. */
export function closeButton(label: string, onClick: () => void): HTMLButtonElement {
  return iconButton(label, onClick);
}

/**
 * Nothing to measure any more: the stack and the popover sit at Blockari's
 * `--column-right`, which the tokens compute from the viewport — the same
 * number the bar's inner column is centred on. Kept as a no-op for the
 * callers that ring it on resize.
 */
export function measureColumn(): void {
  // The column is a token now.
}

/** The chip's tone class per kind: WON mint · LOST rose · TAKEN indigo · EXPIRED plain. */
const CHIP_TONE: Record<NotificationKind, string> = {
  challenge_won: 'w21-mint',
  challenge_lost: 'w21-rose',
  challenge_taken: 'w21-indigo',
  challenge_expired: '',
  challenge_voided: '',
  chest_earned: 'w21-amber',
};

/**
 * What a notification is ABOUT, as a key.
 *
 * Almost every kind is about a challenge, and the stack supersedes by it — two
 * notifications about one challenge are one story. A chest is about a chest,
 * and two of them are two stories, so it keys by its own id and neither
 * replaces the other.
 */
function subjectOf(n: AppNotification): string {
  return n.challengeId ?? n.chestId ?? n.id;
}

/**
 * The toast stack — Blockari's `NotifyToasts`, in the kit's markup.
 *
 * `.notify-stack` fixed at the column's right edge under the bar (bottom
 * centre on a phone); each `.notify-toast.<tone>` is a `button.nt-body`
 * (eyebrow, amount with the coin, line with its suffix, scores) beside the
 * `.nt-close` column, with a 2 px `.nt-hairline` draining across the bottom
 * over six seconds — paused while held, swiped away sideways.
 */
export class Toasts {
  readonly root: HTMLElement;
  readonly #callbacks: ToastCallbacks;
  /** One toast per challenge: a settlement replaces its own "taken". */
  readonly #shown = new Map<string, HTMLElement>();

  constructor(callbacks: ToastCallbacks) {
    this.#callbacks = callbacks;
    this.root = el('div', 'w21-notify-stack');
    this.root.setAttribute('aria-label', 'Notifications');
    this.root.setAttribute('role', 'status');
    this.root.setAttribute('aria-live', 'polite');
  }

  /** Nothing to line up any more; the column is a token. Kept for the resize hook. */
  measure(): void {
    measureColumn();
  }

  /**
   * Stop dismissing toasts, for the frame harness only. A toast lives six
   * seconds, and a capture that races that clock sometimes photographs an
   * empty page.
   */
  hold(): void {
    this.#held = true;
  }

  #held = false;

  show(n: AppNotification): void {
    // Supersession. Two notifications about one challenge are one story, and
    // the newest is the end of it.
    const subject = subjectOf(n);
    const previous = this.#shown.get(subject);
    if (previous) this.#dismiss(previous, subject);

    const toast = this.#build(n);
    this.#shown.set(subject, toast);
    // Newest first.
    this.root.prepend(toast);
    this.#callbacks.onShown?.(n, toast);

    while (this.root.childElementCount > MAX_TOASTS) {
      const oldest = this.root.lastElementChild;
      if (!(oldest instanceof HTMLElement)) break;
      this.#dismiss(oldest, oldest.dataset.challenge ?? '');
    }
  }

  clear(): void {
    this.root.replaceChildren();
    this.#shown.clear();
  }

  /** Take down the toast about one challenge, if one is up. */
  drop(challengeId: string): void {
    const toast = this.#shown.get(challengeId);
    if (toast) this.#dismiss(toast, challengeId);
  }

  #dismiss(toast: HTMLElement, challengeId: string, side: 'left' | 'right' | null = null): void {
    if (this.#shown.get(challengeId) === toast) this.#shown.delete(challengeId);
    if (side) toast.classList.add(side === 'left' ? 'w21-leave-left' : 'w21-leave-right');
    else toast.classList.add('w21-leave-right');
    const remove = (): void => toast.remove();
    toast.addEventListener('animationend', remove, { once: true });
    // An animation that never fires — reduced motion, a backgrounded tab —
    // would leave the node forever.
    window.setTimeout(remove, 400);
  }

  #build(n: AppNotification): HTMLElement {
    const tone = TONE[n.kind];
    const toast = el('div', `w21-notify-toast w21-${tone}`);
    toast.setAttribute('role', 'status');
    // A toast arrives unannounced, often over the very button a thumb is already coming down on, and
    // took the tap (Match me, Start, Play again → "the notification instead"). For its first moments it
    // is transparent to touches, so the tap lands on what the player aimed at.
    toast.classList.add('w21-arming');
    window.setTimeout(() => toast.classList.remove('w21-arming'), TOAST_ARMING_MS);
    toast.dataset.subject = subjectOf(n);
    toast.style.setProperty('--dx', '0px');
    toast.style.setProperty('--left', '1');

    const body = el('button', 'w21-nt-body') as HTMLButtonElement;
    body.type = 'button';
    body.addEventListener('click', (event) => {
      // A mouse drag ends in a click too. Past TAP_PX it was a swipe (or
      // an abandoned one), not a press; the keyboard's click has no travel.
      if (event.detail !== 0 && Math.abs(travelled) >= TAP_PX) return;
      this.#dismiss(toast, subjectOf(n));
      if (n.kind === 'chest_earned') {
        if (n.chestId) this.#callbacks.onOpenChest?.(n.chestId);
      } else if (n.challengeId) {
        this.#callbacks.onOpen(n.challengeId);
      }
    });

    body.append(el('span', 'w21-nt-eyebrow', EYEBROW[n.kind]));
    // The amount is the loudest thing in the toast when there is one. A
    // "taken" has no amount yet and gets none; an expiry's is in its line
    // ("… refunded") and the pill pulses instead, as Blockari's does.
    if (n.amount !== 0 && (n.kind === 'challenge_won' || n.kind === 'challenge_lost')) {
      const amount = el('span', 'w21-nt-amount');
      if (n.amount > 0) amount.append(coin());
      amount.append(document.createTextNode(amountText(n.amount)));
      body.append(amount);
    }
    const { lead, suffix } = sentence(n);
    const line = el('span', 'w21-nt-line', drawnLine(lead));
    // A result is the whole line ("You beat nova") and has no suffix; "· playing now" for a take.
    if (suffix) line.append(el('span', 'w21-nt-suffix', `\u00a0${suffixSep(n)}\u00a0${suffix.replace(/ /g, '\u00a0')}`));
    body.append(line);
    const scores = scoresText(n);
    if (scores) body.append(el('span', 'w21-nt-scores', scores));
    toast.setAttribute('aria-label', summaryText(n));

    const close = dismissControl(() => this.#dismiss(toast, subjectOf(n)));
    const hair = el('i', 'w21-nt-hairline');
    hair.setAttribute('aria-hidden', 'true');
    toast.append(body, close, hair);

    // The hairline IS the timer, so pausing it and pausing the dismissal are
    // the same act: `.held` stops the drain and the clock together.
    let remaining = TOAST_MS;
    let startedAt = Date.now();
    let timer = this.#held ? 0 : window.setTimeout(() => this.#dismiss(toast, subjectOf(n)), remaining);
    const hold = (): void => {
      window.clearTimeout(timer);
      remaining = Math.max(0, remaining - (Date.now() - startedAt));
      toast.classList.add('w21-held');
    };
    const release = (): void => {
      if (this.#held) return;
      startedAt = Date.now();
      toast.classList.remove('w21-held');
      timer = window.setTimeout(() => this.#dismiss(toast, subjectOf(n)), remaining);
    };
    toast.addEventListener('pointerenter', hold);
    toast.addEventListener('pointerleave', release);
    toast.addEventListener('focusin', hold);
    toast.addEventListener('focusout', release);

    // Swipe sideways to dismiss: the toast follows the finger (`--dx`) and
    // leaves the way it was pushed once past 80 px.
    let startX: number | null = null;
    let travelled = 0;
    toast.addEventListener('pointerdown', (event) => {
      startX = event.clientX;
      travelled = 0;
      hold();
    });
    toast.addEventListener('pointermove', (event) => {
      if (startX === null) return;
      const dx = event.clientX - startX;
      toast.style.setProperty('--dx', `${dx}px`);
      toast.style.setProperty('--left', dx < 0 ? '1' : '0');
    });
    const lift = (event: PointerEvent): void => {
      if (startX === null) return;
      const dx = event.clientX - startX;
      startX = null;
      travelled = dx;
      if (Math.abs(dx) > 80) this.#dismiss(toast, subjectOf(n), dx < 0 ? 'left' : 'right');
      else {
        toast.style.setProperty('--dx', '0px');
        release();
      }
    };
    toast.addEventListener('pointerup', lift);
    toast.addEventListener('pointercancel', lift);

    return toast;
  }
}

/* ----------------------------------------------------------------- badge -- */

/** The unread count on the avatar: the bar draws its own dot now; these two stay for the older callers. */
export function unreadBadge(): HTMLElement {
  const badge = el('span', 'w21-unread-dot');
  badge.setAttribute('aria-hidden', 'true');
  badge.hidden = true;
  return badge;
}

export function setUnread(badge: HTMLElement, count: number): void {
  badge.hidden = count <= 0;
  badge.textContent = count > 1 ? (count > 9 ? '9+' : String(count)) : '';
}

/* ----------------------------------------------------------------- inbox -- */

export interface InboxCallbacks {
  onOpen(challengeId: string): void;
  onOpenChest?(chestId: string): void;
  onMarkRead(ids: readonly string[] | undefined): void;
  onProfile(): void;
  onAll(): void;
  /** The phone's way to the leaderboard (the desk has it in the nav). */
  onLeaderboard?(): void;
  onGuide?(): void;
  onSettings?(): void;
}

/**
 * The list behind the avatar — Blockari's `InboxPopover`: a popover under
 * the bar at the column's right edge on a desktop, a bottom sheet on a
 * phone. `.inbox-layer > .inbox-pop > .inbox-head + .inbox-scroll > ul.inbox-list
 * + .inbox-foot`. Escape, a click outside, a row, or a route change closes it.
 */
export class Inbox {
  readonly root: HTMLElement;
  readonly #pop: HTMLElement;
  #list: HTMLElement;
  readonly #mark: HTMLButtonElement;
  readonly #callbacks: InboxCallbacks;
  #readTimer = 0;
  #open = false;

  constructor(callbacks: InboxCallbacks) {
    this.#callbacks = callbacks;
    this.root = el('div', 'w21-inbox-layer');
    this.root.hidden = true;
    this.root.addEventListener('click', (event) => {
      if (event.target === this.root) this.close();
    });

    this.#pop = el('div', 'w21-inbox-pop');
    this.#pop.setAttribute('role', 'dialog');
    this.#pop.setAttribute('aria-labelledby', 'w21-inbox-title');
    this.#pop.tabIndex = -1;

    const head = el('div', 'w21-inbox-head');
    const title = el('h3', undefined, 'Notifications');
    title.id = 'w21-inbox-title';
    const row = el('div', 'w21-row');
    const mark = el('button', 'w21-btn w21-ghost w21-sm', 'Mark all read') as HTMLButtonElement;
    mark.type = 'button';
    mark.addEventListener('click', () => {
      this.#callbacks.onMarkRead(undefined);
      // Say so: the rows keep their rails until the panel closes, so the
      // button going is the confirmation.
      this.#mark.hidden = true;
    });
    this.#mark = mark;
    row.append(mark, iconButton('Close', () => this.close()));
    head.append(title, row);

    const scroll = el('div', 'w21-inbox-scroll');
    this.#list = el('ul', 'w21-inbox-list');
    scroll.append(this.#list);

    const foot = el('div', 'w21-inbox-foot');
    const all = el('a', 'w21-btn w21-sm', 'All notifications') as HTMLAnchorElement;
    all.href = '#';
    all.addEventListener('click', (event) => {
      event.preventDefault();
      this.close();
      this.#callbacks.onAll();
    });
    const profile = el('a', 'w21-inbox-profile', 'Profile') as HTMLAnchorElement;
    profile.href = '#';
    profile.innerHTML += ICON_ARROW;
    profile.addEventListener('click', (event) => {
      event.preventDefault();
      this.close();
      this.#callbacks.onProfile();
    });
    // Blockari's phone entry: the leaderboard, beside the profile.
    const board = el('a', 'w21-inbox-profile', 'Leaderboard') as HTMLAnchorElement;
    board.href = '#';
    board.addEventListener('click', (event) => {
      event.preventDefault();
      this.close();
      this.#callbacks.onLeaderboard?.();
    });
    // Blockari's foot: All notifications, then Leaderboard · Guide · Settings · Profile →.
    const link = (label: string, on: (() => void) | undefined): HTMLAnchorElement | null => {
      if (!on) return null;
      const a = el('a', 'w21-inbox-profile', label) as HTMLAnchorElement;
      a.href = '#';
      a.addEventListener('click', (event) => {
        event.preventDefault();
        this.close();
        on();
      });
      return a;
    };
    const guide = link('Guide', this.#callbacks.onGuide);
    if (guide) guide.title = 'How it works';
    const settings = link('Settings', this.#callbacks.onSettings);
    foot.append(all);
    if (this.#callbacks.onLeaderboard) foot.append(board);
    if (guide) foot.append(guide);
    if (settings) foot.append(settings);
    foot.append(profile);

    this.#pop.append(head, scroll, foot);
    this.root.append(this.#pop);

    this.root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });
  }

  get isOpen(): boolean {
    return this.#open;
  }

  /**
   * @param unread - the account's unread COUNT, which may exceed the rows
   *   listed (the list is a page). "Mark all read" follows it: hidden when
   *   there is nothing to mark, since a control that looks pressable and
   *   changes nothing reads as broken — and shown while older unread remain
   *   below the page, since it marks those too.
   * @param loaded - false until the first fetch has answered; the panel says
   *   "Loading…" rather than "Nothing yet" about a list it has not seen.
   */
  show(items: readonly AppNotification[], unread?: number, loaded = true): void {
    this.#mark.hidden = (unread ?? items.filter((n) => n.readAt === null).length) === 0;
    if (!loaded || items.length === 0) {
      // Blockari's: a `p.inbox-empty` in place of the list, its copy.
      const empty = el(
        'p',
        'w21-inbox-empty',
        loaded
          ? "Nothing yet. You'll hear here when someone takes your challenge and when a match settles."
          : 'Loading\u2026',
      );
      this.#list.replaceWith(empty);
      this.#list = empty;
      return;
    }
    if (this.#list.tagName !== 'UL') {
      const list = el('ul', 'w21-inbox-list');
      this.#list.replaceWith(list);
      this.#list = list;
    }
    this.#list.replaceChildren(...items.map((n) => this.#row(n)));
  }

  /**
   * The unread count changed while the panel is open (the auto-read after open, a new arrival, a
   * resync). Blockari's "Mark all read" renders only while unread > 0, so it follows the live
   * count; the rows are deliberately not re-rendered, so their rails stay.
   */
  syncUnread(unread: number): void {
    this.#mark.hidden = unread === 0;
  }

  open(items: readonly AppNotification[], unread?: number, loaded = true): void {
    this.show(items, unread, loaded);
    this.root.hidden = false;
    this.#open = true;
    // The page under a sheet does not scroll while it is open.
    document.documentElement.classList.add('w21-inbox-open');
    requestAnimationFrame(() => this.#pop.focus({ preventScroll: true }));

    // Read after a beat, not on open: the rails stay for as long as the
    // panel is open, so a player who opens it to read them can.
    window.clearTimeout(this.#readTimer);
    const visible = items.filter((n) => n.readAt === null).map((n) => n.id);
    // The inbox lists the newest page; the count covers every row. When the server counts older
    // unread rows than the list shows, the reader has seen everything they can reach: mark all
    // (otherwise those rows keep the dot and the icon badge lit forever).
    const plan = seenPlan(visible, unread ?? visible.length);
    if (plan !== null) {
      this.#readTimer = window.setTimeout(
        () => this.#callbacks.onMarkRead(plan === 'all' ? undefined : plan),
        1000,
      );
    }
  }

  close(): void {
    window.clearTimeout(this.#readTimer);
    if (!this.#open) return;
    this.#open = false;
    this.root.hidden = true;
    document.documentElement.classList.remove('w21-inbox-open');
    // Focus returns to the avatar that opened it.
    const avatar = document.querySelector<HTMLElement>('.w21-screen[data-active="true"] .w21-avatar-link');
    avatar?.focus({ preventScroll: true });
  }

  #row(n: AppNotification): HTMLElement {
    const row = el('li', `w21-inbox-row w21-${TONE[n.kind]}${n.readAt === null ? ' w21-unread' : ''}`);
    const link = el('a') as HTMLAnchorElement;
    link.href = '#';
    link.addEventListener('click', (event) => {
      event.preventDefault();
      this.close();
      if (n.kind === 'chest_earned') {
        if (n.chestId) this.#callbacks.onOpenChest?.(n.chestId);
      } else if (n.challengeId) {
        this.#callbacks.onOpen(n.challengeId);
      }
    });

    link.append(el('span', `w21-chip${CHIP_TONE[n.kind] ? ` ${CHIP_TONE[n.kind]}` : ''}`, CHIP[n.kind].toUpperCase()));

    const main = el('span', 'w21-ir-main');
    if (n.amount !== 0) {
      const amount = el('b', 'w21-ir-amount');
      if (n.amount > 0 && n.kind !== 'challenge_expired') amount.append(coin());
      amount.append(document.createTextNode(amountText(n.amount)));
      main.append(amount);
    }
    main.append(el('span', 'w21-ir-line', drawnLine(sentence(n).lead)));
    link.append(main);

    const side = el('span', 'w21-ir-side');
    const scores = scoresText(n);
    if (scores) side.append(el('span', 'w21-ir-scores', scores));
    const time = el('time', undefined, when(n.createdAt));
    time.setAttribute('datetime', n.createdAt);
    side.append(time);
    link.append(side);

    row.append(link);
    return row;
  }
}
